import React from 'react';
import styles from './Flex.module.scss';
import Element, { ElementProps } from '../Element/Element';

type TagName = keyof JSX.IntrinsicElements;

export type FlexProps<T extends TagName> = {
  /** Center flex items */
  centered?: boolean;
  /** Center flex items horizontally */
  centeredHorizontally?: boolean;
  /** Center flex items vertically */
  centeredVertically?: boolean;
  /** Align flex items to top */
  alignTop?: boolean;
  /** Align flex items to bottom */
  alignBottom?: boolean;
  /** Align flex items vertically to baseline */
  alignBaseline?: boolean;
  /** Align flex items to right */
  alignRight?: boolean;
  /** Add space between flex items */
  spaceBetween?: boolean;
  /** Wrap flex items */
  wrapContent?: boolean;
  /** Pile flex items vertically */
  pileVerticallyTo?: 'xxxs' | 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xlg';
  /** Set flex items height to 100% occupying possible space */
  occupyVerticalSpace?: boolean;
  /** Set every flex item to be same height */
  stretchToSameHeight?: boolean;
  /** Reverse flex items direction */
  reverseDirection?: boolean;
  /** Changes the flex direction from row to column */
  directionColumn?: boolean;
  /** HTML tag name: div, span, h1 etc */
  tagName?: T;
} & Omit<ElementProps<T>, 'tagName' | 'occupyVerticalSpace'>;

function Flex<T extends TagName>({
  className,
  children,
  centered,
  centeredVertically = centered,
  centeredHorizontally = centered,
  alignTop,
  alignBottom,
  alignBaseline,
  alignRight,
  spaceBetween,
  wrapContent,
  pileVerticallyTo,
  occupyVerticalSpace = true,
  stretchToSameHeight,
  reverseDirection,
  directionColumn,
  tagName = 'div' as T,
  ...props
}: FlexProps<T>) {
  const classNames = [
    styles.flex,
    {
      [styles.flex__centeredVertically]: centeredVertically,
      [styles.flex__centeredHorizontally]: centeredHorizontally,
      [styles.flex__alignTop]: alignTop,
      [styles.flex__alignBottom]: alignBottom,
      [styles.flex__alignBaseline]: alignBaseline,
      [styles.flex__alignRight]: alignRight,
      [styles.flex__spaceBetween]: spaceBetween,
      [styles.flex__wrapContent]: wrapContent,
      [styles.flex__stretchToSameHeight]: stretchToSameHeight,
      [styles.flex__pileVerticallyToXxxsScreen]: pileVerticallyTo === 'xxxs',
      [styles.flex__pileVerticallyToXxsScreen]: pileVerticallyTo === 'xxs',
      [styles.flex__pileVerticallyToXsScreen]: pileVerticallyTo === 'xs',
      [styles.flex__pileVerticallyToSmScreen]: pileVerticallyTo === 'sm',
      [styles.flex__pileVerticallyToMdScreen]: pileVerticallyTo === 'md',
      [styles.flex__pileVerticallyToLgScreen]: pileVerticallyTo === 'lg',
      [styles.flex__pileVerticallyToXlgScreen]: pileVerticallyTo === 'xlg',
      [styles.flex__occupyVerticalSpace]: occupyVerticalSpace,
      [styles.flex__reverseDirection]: reverseDirection,
      [styles.flex__directionColumn]: directionColumn,
    },
    className,
  ];

  return (
    <Element tagName={tagName as TagName} className={classNames} {...props}>
      {children}
    </Element>
  );
}

export default Flex;
